/**
 * Shared tooltip content for Readiness Score and its components.
 * Used across athlete detail views, coach dashboards, and team assessments.
 */

/**
 * High-level tooltip explaining how the overall Readiness Score is calculated.
 */
export const READINESS_SCORE_TOOLTIP = {
  title: 'About Readiness Score',
  body: 'A comprehensive measure of training preparedness calculated from multiple physiological factors including heart rate variability (HRV), resting heart rate, sleep quality and duration, and training load balance. Each metric is compared against personal baselines to provide a personalized assessment of recovery status.',
};

/**
 * Plain-English tooltip content for each readiness component.
 * Explains how each metric is calculated and what optimal values look like.
 */
export const COMPONENT_TOOLTIPS: Record<string, { title: string; body: string }> = {
  HRV: {
    title: 'HRV (Heart Rate Variability)',
    body: 'Compared to your last 14 days. Above your average scores higher; below or jumpy HRV scores lower. Optimal: at or above your personal baseline.',
  },
  RHR: {
    title: 'Resting Heart Rate',
    body: 'Compared to your last 14 days. Lower than baseline adds points; higher subtracts (~5 per bpm). Optimal: at or below your personal baseline.',
  },
  Sleep: {
    title: 'Sleep',
    body: 'Mostly how last night compares to your usual (14 days); a small part is duration. Optimal: at or above your usual. Personal baseline only.',
  },
  ACWR: {
    title: 'ACWR (Acute:Chronic Workload Ratio)',
    body: 'Only component not personal-baseline. Acute load (7 days) ÷ chronic (28 days). Optimal: 0.80–1.30 (sweet spot 1.0–1.1). Above 1.30 = higher injury risk.',
  },
  Load: {
    title: 'Load (ACWR or Strain)',
    body: 'Training load balance. ACWR (Garmin): Optimal 0.80–1.30. Strain (WHOOP): Optimal 8–14. Higher values indicate increased fatigue or injury risk.',
  },
  Wellness: {
    title: 'Wellness',
    body: 'Mood, stress, soreness combined. Optimal when reported wellness is high. Personal patterns when available.',
  },
};
